import {
    Component,
    forwardRef,
    OnDestroy,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Permission, PermissionGroup } from '@masar/common/models';
import { LicenseFeatureService, MiscApiService } from '@masar/core/services';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { functions } from '@masar/common/misc/functions';
import { isOrigin } from '@masar/common/utils';
import { OrganizationOrigin } from '@masar/common/enums';
import { permissionList } from '@masar/common/constants';

interface PermissionCategory {
    id: string;
    permissions: PermissionWithCheckboxStatus[];
}

interface PermissionWithCheckboxStatus {
    permission: Permission;
    checked: boolean;
    disabled: boolean;
    disabledByGroup?: boolean;
    disabledByGroupNames?: string[];
}

@Component({
    selector: 'app-permission-field',
    templateUrl: './permission-field.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PermissionFieldComponent),
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PermissionFieldComponent
    implements ControlValueAccessor, OnDestroy, OnChanges
{
    @Input() public selectedPermissionGroupIds: string[] = [];
    @Input() public userId: string; // The user being edited
    @Input() public fullAccessGuardFn: (userId: string) => Promise<boolean>;

    public isLoading = true;
    public items$ = new BehaviorSubject<PermissionCategory[]>(null);
    private unsubscribeAll = new Subject<void>();

    private overridenMap = new Map<string, PermissionWithCheckboxStatus[]>();
    private permissionGroupsMap = new Map<string, PermissionGroup>();
    private onChange: (data: string[]) => void;

    /**
     * Maps permission category IDs (from id.split(':')[0]) to license
     * feature strings. Categories not listed here are always visible.
     */
    private static readonly permissionCategoryFeatureMap = new Map<
        string,
        string
    >([
        ['kpi', 'kpis'],
        ['kpi_annual_transition', 'kpis'],
        ['kpi_result', 'kpis'],
        ['kpi_result_entry_request', 'kpis'],
        ['kpi_result_target_setting_method', 'kpis'],
        ['kpi_result_capability_type', 'kpis'],
        ['kpi_tag', 'kpis'],
        ['kpi_type', 'kpis'],
        ['kpi_balanced_behavior_card', 'kpis'],
        ['kpi_result_category', 'kpis'],
        ['report', 'kpis'],
        ['strategic_plan', 'kpis'],
        ['strategic_goal', 'kpis'],
        ['strategic_special_goal', 'kpis'],
        ['government_strategic_goal', 'kpis'],
        ['plan', 'plans'],
        ['plan_input', 'plans'],
        ['plan_category', 'plans'],
        ['plan_task_category', 'plans'],
        ['operation', 'operations'],
        ['operation_rule_and_regulation', 'operations'],
        ['operation_specification', 'operations'],
        ['operation_enhancement_type', 'operations'],
        ['benchmark', 'benchmarks'],
        ['project', 'projects'],
        ['partner', 'partners'],
        ['partnership', 'partners'],
        ['service', 'services'],
        ['service_category', 'services'],
        ['improvement_opportunity', 'improvement_opportunities'],
        ['improvement_opportunity_input_source', 'improvement_opportunities'],
        ['improvement_opportunity_input_category', 'improvement_opportunities'],
        ['improvement_opportunity_input', 'improvement_opportunities'],
        ['suggestion', 'suggestions'],
        ['suggestion_channel', 'suggestions'],
        ['statistical_report', 'statistical_reports'],
        ['tournament', 'tournaments'],
        ['risk', 'risks'],
        ['committee', 'committees'],
        ['meeting', 'committees'],
        ['capability', 'capabilities'],
        ['capability_type', 'capabilities'],
        ['team', 'teams'],
    ]);

    /**
     * Maps specific permission IDs to license feature strings,
     * for cases where the category is shared across features.
     */
    private static readonly permissionIdFeatureMap = new Map<string, string>();

    public constructor(
        private miscApiService: MiscApiService,
        private licenseFeatureService: LicenseFeatureService
    ) {
        // Load permission groups first, then permissions
        combineLatest([
            this.miscApiService.permissions(),
            this.miscApiService.permissionGroups(),
        ])
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(([permissions, permissionGroups]) => {
                // Store permission groups for quick lookup
                permissionGroups.forEach(group => {
                    this.permissionGroupsMap.set(group.id, group);
                });

                const items = permissions
                    .filter(
                        x =>
                            !isOrigin([
                                OrganizationOrigin.injaz,
                                OrganizationOrigin.police,
                            ]) || !x.id.includes('risk')
                    )
                    .filter(x => this.isPermissionLicensed(x))
                    .map(permission => ({
                        checked: false,
                        disabled: false,
                        disabledByGroup: false,
                        disabledByGroupNames: [],
                        permission,
                    }));

                items.forEach(x => {
                    this.overridenMap.set(
                        x.permission.id,
                        this.getAllOverridenIdList(x.permission.id, items)
                    );
                });

                const categorizedItems = items.map(x => ({
                    categoryId: x.permission.id.split(':')[0],
                    permission: x,
                }));
                const categorizedPermissions = functions
                    .groupBy(categorizedItems, x => x.categoryId)
                    .map(x => ({
                        id: x.key,
                        permissions: x.items.map(y => y.permission),
                    }));

                this.items$.next(categorizedPermissions);

                // Apply group-based disabling if groups are already selected
                this.updatePermissionGroupDisabling();

                // Apply cascading disable logic for initial state
                this.applyCascadingDisable(categorizedPermissions);

                this.isLoading = false;
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.selectedPermissionGroupIds &&
            !changes.selectedPermissionGroupIds.firstChange
        ) {
            this.updatePermissionGroupDisabling();
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
        this.items$.complete();
    }

    public writeValue(data: string[]): void {
        if (!data || data.length === 0) return;

        // Always use getValue() for BehaviorSubject when we just need current value
        const currentItems = this.items$.getValue();
        if (currentItems) {
            // Items already loaded, process immediately
            data.forEach(itemId => {
                const item = currentItems
                    .flatMap(x => x.permissions)
                    .find(x => x.permission.id === itemId);
                if (item) {
                    this.onToggle(item, true, false);
                }
            });
        } else {
            // Items not loaded yet, wait for them (rare case during initial load)
            this.items$
                .pipe(
                    filter(x => x !== null),
                    first(),
                    takeUntil(this.unsubscribeAll)
                )
                .subscribe(items => {
                    data.forEach(itemId => {
                        const item = items
                            .flatMap(x => x.permissions)
                            .find(x => x.permission.id === itemId);
                        if (item) {
                            this.onToggle(item, true, false);
                        }
                    });
                });
        }
    }

    public registerOnChange(onChange: (data: string[]) => void): void {
        this.onChange = onChange;
    }

    public registerOnTouched(_: any): void {
        return undefined;
    }

    public async onCheckboxClick(
        event: MouseEvent,
        item: PermissionWithCheckboxStatus
    ): Promise<void> {
        // Only prevent default for full access permission when unchecking for current user
        if (this.shouldConfirmFullAccessRemoval(item)) {
            event.preventDefault();
        }

        await this.onToggle(item, !item.checked);
    }

    public async onToggle(
        item: PermissionWithCheckboxStatus,
        checked: boolean,
        reportChange: boolean = true
    ): Promise<void> {
        if (!item) return;

        if (this.shouldConfirmFullAccessRemoval(item) && !checked) {
            const isConfirmed = this.fullAccessGuardFn
                ? await this.fullAccessGuardFn(this.userId)
                : true;
            if (!isConfirmed) {
                return;
            }
        }

        item.checked = checked;

        const items = this.items$.getValue();
        if (!items) return;

        // Handle the overridden permissions
        const overridenPermissions = this.overridenMap.get(item.permission.id);
        if (overridenPermissions) {
            if (checked) {
                overridenPermissions.forEach(x => {
                    x.checked = false;
                    x.disabled = true;
                });
            } else {
                overridenPermissions.forEach(x => {
                    if (!x.disabledByGroup) {
                        x.disabled = false;
                    }
                });
            }
        }

        this.applyCascadingDisable(items);

        if (this.onChange && reportChange) {
            this.onChange(
                items
                    .flatMap(x => x.permissions)
                    .filter(x => x.checked)
                    .map(x => x.permission.id)
            );
        }
    }

    public trackByPermissionId(
        _index: number,
        item: PermissionWithCheckboxStatus
    ): string {
        return item.permission.id;
    }

    private shouldConfirmFullAccessRemoval(
        item: PermissionWithCheckboxStatus
    ): boolean {
        return item.permission.id === permissionList.fullAccess && item.checked;
    }

    private getAllOverridenIdList(
        permissionId: string,
        items: PermissionWithCheckboxStatus[]
    ): PermissionWithCheckboxStatus[] {
        return items.filter(x =>
            x.permission.overriders.includes(permissionId)
        );
    }

    private updatePermissionGroupDisabling(): void {
        // Always use getValue() for synchronous access
        const items = this.items$.getValue();
        if (!items) return;

        if (
            !this.selectedPermissionGroupIds ||
            this.selectedPermissionGroupIds.length === 0
        ) {
            // No groups selected, remove all group-based disabling
            items.forEach(category => {
                category.permissions.forEach(permission => {
                    permission.disabledByGroup = false;
                    permission.disabledByGroupNames = [];
                    permission.disabled = false;
                });
            });

            // Apply cascading disable logic for any remaining checked permissions
            this.applyCascadingDisable(items);
            return;
        }

        // Get all permissions from selected groups
        const groupPermissions = new Map<string, string[]>(); // permissionId -> groupNames

        this.selectedPermissionGroupIds.forEach(groupId => {
            const group = this.permissionGroupsMap.get(groupId);

            // Use permissionList (from backend) or permissionIdList (fallback)
            const permissions =
                group?.permissionList || group?.permissionIdList;
            if (group && permissions) {
                permissions.forEach(permissionId => {
                    if (!groupPermissions.has(permissionId)) {
                        groupPermissions.set(permissionId, []);
                    }
                    groupPermissions.get(permissionId)!.push(group.name);
                });
            }
        });

        // Update permission states
        // First pass: Set group-based disabling
        items.forEach(category => {
            category.permissions.forEach(permission => {
                const groupNames = groupPermissions.get(
                    permission.permission.id
                );
                if (groupNames && groupNames.length > 0) {
                    // This permission is covered by selected groups
                    permission.disabledByGroup = true;
                    permission.disabledByGroupNames = groupNames;
                    permission.disabled = true;
                    permission.checked = false; // Uncheck if it was checked
                } else {
                    // This permission is not covered by groups
                    permission.disabledByGroup = false;
                    permission.disabledByGroupNames = [];
                    permission.disabled = false;
                }
            });
        });

        // Second pass: Apply cascading disable logic for overriders
        this.applyCascadingDisable(items);

        // Report the change to remove unchecked permissions
        if (this.onChange) {
            this.onChange(
                items
                    .flatMap(x => x.permissions)
                    .filter(x => x.checked)
                    .map(x => x.permission.id)
            );
        }
    }

    private isPermissionLicensed(permission: Permission): boolean {
        // Check individual permission ID first (for mixed categories)
        const idFeature = PermissionFieldComponent.permissionIdFeatureMap.get(
            permission.id
        );
        if (idFeature) {
            return this.licenseFeatureService.hasFeature(idFeature);
        }

        // Check category
        const category = permission.id.split(':')[0];
        const categoryFeature =
            PermissionFieldComponent.permissionCategoryFeatureMap.get(category);
        if (categoryFeature) {
            return this.licenseFeatureService.hasFeature(categoryFeature);
        }

        // No mapping = always visible
        return true;
    }

    private applyCascadingDisable(items: PermissionCategory[]): void {
        let hasChanges = true;

        // Keep applying cascading disable until no more changes occur
        while (hasChanges) {
            hasChanges = false;

            items.forEach(category => {
                category.permissions.forEach(permission => {
                    // Skip if already disabled by group
                    if (permission.disabledByGroup) {
                        return;
                    }

                    // Check if any of this permission's overriders are checked or disabled
                    // If an overrider permission is checked or disabled, this permission should be disabled
                    const hasCheckedOrDisabledOverrider =
                        permission.permission.overriders.some(overriderId => {
                            const overriderPermission = items
                                .flatMap(cat => cat.permissions)
                                .find(p => p.permission.id === overriderId);
                            return (
                                overriderPermission?.checked === true ||
                                overriderPermission?.disabled === true
                            );
                        });

                    if (hasCheckedOrDisabledOverrider && !permission.disabled) {
                        permission.disabled = true;
                        permission.checked = false; // Uncheck if it was checked
                        hasChanges = true;
                    } else if (
                        !hasCheckedOrDisabledOverrider &&
                        permission.disabled &&
                        !permission.disabledByGroup
                    ) {
                        // Re-enable if no overriders are checked or disabled and not disabled by group
                        permission.disabled = false;
                        hasChanges = true;
                    }
                });
            });
        }
    }
}
